import { FC, useEffect, useState, useCallback } from 'react';
import { adminService } from '@/services/admin.service';
import { useToast } from '@/contexts/ToastContext';
import type { AdminSector, SectorUser } from '@/types/admin';

const AdminSectorsPage: FC = () => {
  const { showToast } = useToast();
  const [sectors, setSectors] = useState<AdminSector[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalAllocated, setTotalAllocated] = useState(0);
  const [unassignedCount, setUnassignedCount] = useState(0);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSector, setEditingSector] = useState<AdminSector | null>(null);
  const [deletingSector, setDeletingSector] = useState<AdminSector | null>(null);

  // Expanded sector (show users)
  const [expandedSectorId, setExpandedSectorId] = useState<string | null>(null);
  const [sectorUsers, setSectorUsers] = useState<SectorUser[]>([]);
  const [sectorUsersLoading, setSectorUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  // Unassigned panel
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [unassignedUsers, setUnassignedUsers] = useState<SectorUser[]>([]);
  const [unassignedLoading, setUnassignedLoading] = useState(false);
  const [unassignedSearch, setUnassignedSearch] = useState('');

  // Selection for transfer
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [showTransferDropdown, setShowTransferDropdown] = useState(false);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formOrder, setFormOrder] = useState(0);
  const [formAgentFeatureEnabled, setFormAgentFeatureEnabled] = useState(false);
  const [formAgentFeatureReason, setFormAgentFeatureReason] = useState('');
  const [formSeopFeatureEnabled, setFormSeopFeatureEnabled] = useState(false);
  const [formSeopFeatureReason, setFormSeopFeatureReason] = useState('');
  const [formDashboardFeatureEnabled, setFormDashboardFeatureEnabled] = useState(false);
  const [formDashboardFeatureReason, setFormDashboardFeatureReason] = useState('');
  const [formSaving, setFormSaving] = useState(false);
  const [formNameError, setFormNameError] = useState('');

  const loadSectors = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await adminService.getSectors();
      setSectors(data);
      setTotalAllocated(data.reduce((acc, s) => acc + s.users_count, 0));
      const unassigned = await adminService.getUnassignedUsers();
      setUnassignedCount(unassigned.count);
    } catch {
      showToast('Erro ao carregar setores', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadSectors();
  }, [loadSectors]);

  const loadSectorUsers = useCallback(async (sectorId: string, search?: string) => {
    setSectorUsersLoading(true);
    try {
      const resp = await adminService.getSectorUsers(sectorId, { search });
      setSectorUsers(resp.results);
    } catch {
      showToast('Erro ao carregar usuários do setor', 'error');
    } finally {
      setSectorUsersLoading(false);
    }
  }, [showToast]);

  const loadUnassignedUsers = useCallback(async (search?: string) => {
    setUnassignedLoading(true);
    try {
      const resp = await adminService.getUnassignedUsers({ search });
      setUnassignedUsers(resp.results);
      setUnassignedCount(resp.count);
    } catch {
      showToast('Erro ao carregar usuários não alocados', 'error');
    } finally {
      setUnassignedLoading(false);
    }
  }, [showToast]);

  const handleExpandSector = (sectorId: string) => {
    setSelectedUserIds(new Set());
    setShowTransferDropdown(false);
    setShowUnassigned(false);
    if (expandedSectorId === sectorId) {
      setExpandedSectorId(null);
      return;
    }
    setExpandedSectorId(sectorId);
    setUserSearch('');
    loadSectorUsers(sectorId);
  };

  const handleShowUnassigned = () => {
    setSelectedUserIds(new Set());
    setShowTransferDropdown(false);
    setExpandedSectorId(null);
    setShowUnassigned(!showUnassigned);
    if (!showUnassigned) {
      setUnassignedSearch('');
      loadUnassignedUsers();
    }
  };

  // Create / Edit
  const openCreateModal = () => {
    setFormName('');
    setFormDescription('');
    setFormOrder(0);
    setFormAgentFeatureEnabled(false);
    setFormAgentFeatureReason('');
    setFormSeopFeatureEnabled(false);
    setFormSeopFeatureReason('');
    setFormDashboardFeatureEnabled(false);
    setFormDashboardFeatureReason('');
    setFormNameError('');
    setEditingSector(null);
    setShowCreateModal(true);
  };

  const openEditModal = (sector: AdminSector) => {
    setFormName(sector.name);
    setFormDescription(sector.description);
    setFormOrder(sector.order);
    setFormAgentFeatureEnabled(sector.agent_feature_enabled);
    setFormAgentFeatureReason('');
    setFormSeopFeatureEnabled(sector.seop_feature_enabled);
    setFormSeopFeatureReason('');
    setFormDashboardFeatureEnabled(sector.dashboard_feature_enabled);
    setFormDashboardFeatureReason('');
    setFormNameError('');
    setEditingSector(sector);
    setShowCreateModal(true);
  };

  const handleSave = async () => {
    const trimmedName = formName.trim();
    if (!trimmedName) return;

    // Client-side duplicate check (case-insensitive)
    const duplicate = sectors.find(
      (s) =>
        s.name.toLowerCase() === trimmedName.toLowerCase() &&
        s.id !== editingSector?.id
    );
    if (duplicate) {
      setFormNameError('Já existe um setor com este nome.');
      return;
    }

    setFormNameError('');
    setFormSaving(true);
    try {
      if (editingSector) {
        await adminService.updateSector(editingSector.id, {
          name: trimmedName,
          description: formDescription.trim(),
          order: formOrder,
          agent_feature_enabled: formAgentFeatureEnabled,
          agent_feature_reason: formAgentFeatureReason || undefined,
          seop_feature_enabled: formSeopFeatureEnabled,
          seop_feature_reason: formSeopFeatureReason || undefined,
          dashboard_feature_enabled: formDashboardFeatureEnabled,
          dashboard_feature_reason: formDashboardFeatureReason || undefined,
        });
        showToast('Setor atualizado!', 'success');
      } else {
        await adminService.createSector({
          name: trimmedName,
          description: formDescription.trim(),
          order: formOrder,
          agent_feature_enabled: formAgentFeatureEnabled,
          seop_feature_enabled: formSeopFeatureEnabled,
          dashboard_feature_enabled: formDashboardFeatureEnabled,
        });
        showToast('Setor criado!', 'success');
      }
      setShowCreateModal(false);
      loadSectors();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: { errors?: Record<string, string[]> } } } };
      const fieldErrors = axiosErr?.response?.data?.error?.errors;
      if (fieldErrors?.name) {
        setFormNameError(fieldErrors.name[0]);
      } else {
        showToast('Erro ao salvar setor.', 'error');
      }
    } finally {
      setFormSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingSector) return;
    try {
      const resp = await adminService.deleteSector(deletingSector.id);
      showToast(resp.message, 'success');
      setDeletingSector(null);
      setExpandedSectorId(null);
      loadSectors();
    } catch {
      showToast('Erro ao excluir setor', 'error');
    }
  };

  // Transfer
  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleTransfer = async (targetSectorId: string | null) => {
    if (selectedUserIds.size === 0) return;
    try {
      const resp = await adminService.transferUsers({
        user_ids: Array.from(selectedUserIds),
        target_sector_id: targetSectorId,
      });
      showToast(resp.message, 'success');
      setSelectedUserIds(new Set());
      setShowTransferDropdown(false);
      loadSectors();
      if (expandedSectorId) loadSectorUsers(expandedSectorId);
      if (showUnassigned) loadUnassignedUsers();
    } catch {
      showToast('Erro ao transferir usuários', 'error');
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  // User search with debounce
  useEffect(() => {
    if (!expandedSectorId) return;
    const timer = setTimeout(() => loadSectorUsers(expandedSectorId, userSearch), 300);
    return () => clearTimeout(timer);
  }, [userSearch, expandedSectorId, loadSectorUsers]);

  useEffect(() => {
    if (!showUnassigned) return;
    const timer = setTimeout(() => loadUnassignedUsers(unassignedSearch), 300);
    return () => clearTimeout(timer);
  }, [unassignedSearch, showUnassigned, loadUnassignedUsers]);

  const renderUserList = (users: SectorUser[], loading: boolean) => (
    <div className="mt-3">
      {loading ? (
        <p className="text-content-muted text-sm py-4 text-center">Carregando...</p>
      ) : users.length === 0 ? (
        <p className="text-content-muted text-sm py-4 text-center">Nenhum usuário encontrado.</p>
      ) : (
        <div className="space-y-1">
          {users.map(user => (
            <label
              key={user.id}
              className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-surface-hover cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedUserIds.has(user.id)}
                onChange={() => toggleUserSelection(user.id)}
                className="rounded border-theme-border"
              />
              <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center text-accent text-xs font-bold shrink-0">
                {user.avatar ? (
                  <img src={user.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  user.name.charAt(0).toUpperCase()
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user.name}</p>
                <p className="text-xs text-content-muted truncate">{user.email}</p>
              </div>
              <span className="text-xs text-content-muted">{formatDate(user.created_at)}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );

  const renderTransferBar = () => {
    if (selectedUserIds.size === 0) return null;
    return (
      <div className="sticky bottom-0 bg-surface-card border-t border-theme-border p-3 flex items-center gap-3">
        <span className="text-sm text-content-muted">{selectedUserIds.size} selecionado(s)</span>
        <div className="relative">
          <button
            onClick={() => setShowTransferDropdown(!showTransferDropdown)}
            className="bg-accent hover:bg-accent-hover text-accent-contrast px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
          >
            <span className="material-symbols-outlined text-lg">swap_horiz</span>
            Transferir para...
          </button>
          {showTransferDropdown && (
            <div className="absolute bottom-full left-0 mb-1 bg-surface-card border border-theme-border rounded-lg shadow-xl min-w-[200px] max-h-[300px] overflow-y-auto z-10">
              <button
                onClick={() => handleTransfer(null)}
                className="w-full text-left px-4 py-2 text-sm hover:bg-surface-hover text-amber-400 border-b border-theme-border"
              >
                Desalocar (sem setor)
              </button>
              {sectors.map(s => (
                <button
                  key={s.id}
                  onClick={() => handleTransfer(s.id)}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-surface-hover text-content-primary"
                  disabled={s.id === expandedSectorId}
                >
                  {s.name} ({s.users_count})
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={() => { setSelectedUserIds(new Set()); setShowTransferDropdown(false); }}
          className="text-content-muted hover:text-content-primary text-sm"
        >
          Cancelar
        </button>
      </div>
    );
  };

  return (
    <div className="p-6 lg:p-10">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <h1 className="text-2xl font-bold">Setores</h1>
        <button
          onClick={openCreateModal}
          className="bg-accent hover:bg-accent-hover text-accent-contrast px-5 py-2.5 rounded-xl font-medium transition-colors flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-xl">add</span>
          Novo Setor
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-surface-card rounded-xl p-5 border border-theme-border">
          <p className="text-content-muted text-sm">Total de Setores</p>
          <p className="text-2xl font-bold mt-1">{sectors.length}</p>
        </div>
        <div className="bg-surface-card rounded-xl p-5 border border-theme-border">
          <p className="text-content-muted text-sm">Usuários Alocados</p>
          <p className="text-2xl font-bold mt-1">{totalAllocated}</p>
        </div>
        <button
          onClick={handleShowUnassigned}
          className={`bg-surface-card rounded-xl p-5 border text-left transition-colors ${
            unassignedCount > 0 ? 'border-amber-500/50 hover:border-amber-500' : 'border-theme-border'
          }`}
        >
          <p className="text-content-muted text-sm">Não Alocados</p>
          <p className={`text-2xl font-bold mt-1 ${unassignedCount > 0 ? 'text-amber-400' : ''}`}>
            {unassignedCount}
          </p>
        </button>
      </div>

      {/* Sectors Table */}
      {isLoading ? (
        <p className="text-content-muted">Carregando...</p>
      ) : sectors.length === 0 ? (
        <div className="text-center py-16 text-content-muted">
          <span className="material-symbols-outlined text-5xl mb-3 block">domain</span>
          <p>Nenhum setor cadastrado.</p>
          <p className="text-sm mt-1">Crie o primeiro setor para organizar os usuários.</p>
        </div>
      ) : (
        <div className="bg-surface-card rounded-xl border border-theme-border overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-theme-border text-left text-content-muted text-sm">
                <th className="px-5 py-3 font-medium">Nome</th>
                <th className="px-5 py-3 font-medium hidden md:table-cell">Descrição</th>
                <th className="px-5 py-3 font-medium text-center">Usuários</th>
                <th className="px-5 py-3 font-medium text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {sectors.map(sector => (
                <>
                  <tr
                    key={sector.id}
                    data-testid="sector-row"
                    className={`border-b border-theme-border/50 hover:bg-surface-hover/50 cursor-pointer ${
                      expandedSectorId === sector.id ? 'bg-surface-hover/30' : ''
                    }`}
                    onClick={() => handleExpandSector(sector.id)}
                  >
                    <td className="px-5 py-4 font-medium">
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-lg text-content-muted">
                          {expandedSectorId === sector.id ? 'expand_more' : 'chevron_right'}
                        </span>
                        {sector.name}
                        {!sector.is_active && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-400">Inativo</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-content-muted hidden md:table-cell truncate max-w-[300px]">
                      {sector.description || '-'}
                    </td>
                    <td className="px-5 py-4 text-center">
                      <span className="text-sm font-medium">{sector.users_count}</span>
                    </td>
                    <td className="px-5 py-4 text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditModal(sector)}
                          className="p-2 rounded-lg hover:bg-surface-hover text-content-muted hover:text-content-primary transition-colors"
                          title="Editar"
                        >
                          <span className="material-symbols-outlined text-xl">edit</span>
                        </button>
                        <button
                          onClick={() => setDeletingSector(sector)}
                          className="p-2 rounded-lg hover:bg-surface-hover text-content-muted hover:text-red-400 transition-colors"
                          title="Excluir"
                        >
                          <span className="material-symbols-outlined text-xl">delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedSectorId === sector.id && (
                    <tr key={`${sector.id}-users`}>
                      <td colSpan={4} className="px-5 py-3 bg-surface-primary/50">
                        <input
                          type="text"
                          placeholder="Buscar por nome ou email..."
                          value={userSearch}
                          onChange={e => setUserSearch(e.target.value)}
                          className="bg-surface-card border border-theme-border rounded-lg px-3 py-2 text-sm w-full max-w-sm mb-2 text-content-primary placeholder-content-muted"
                          onClick={e => e.stopPropagation()}
                        />
                        {renderUserList(sectorUsers, sectorUsersLoading)}
                        {renderTransferBar()}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Unassigned Users Panel */}
      {showUnassigned && (
        <div data-testid="unassigned-users" className="mt-6 bg-surface-card rounded-xl border border-amber-500/30 overflow-hidden">
          <div className="px-5 py-4 border-b border-theme-border flex items-center gap-2">
            <span className="material-symbols-outlined text-amber-400">warning</span>
            <h3 className="font-medium">Usuários Não Alocados</h3>
          </div>
          <div className="p-5">
            <input
              type="text"
              placeholder="Buscar por nome ou email..."
              value={unassignedSearch}
              onChange={e => setUnassignedSearch(e.target.value)}
              className="bg-surface-card border border-theme-border rounded-lg px-3 py-2 text-sm w-full max-w-sm mb-2 text-content-primary placeholder-content-muted"
            />
            {unassignedCount === 0 && !unassignedLoading ? (
              <p className="text-content-muted text-sm py-4 text-center">
                Todos os usuários estão alocados em setores
              </p>
            ) : (
              renderUserList(unassignedUsers, unassignedLoading)
            )}
            {renderTransferBar()}
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface-card rounded-2xl border border-theme-border w-full max-w-md p-6">
            <h2 className="text-lg font-bold mb-4">
              {editingSector ? 'Editar Setor' : 'Novo Setor'}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-content-muted mb-1">Nome *</label>
                <input
                  type="text"
                  value={formName}
                  onChange={e => { setFormName(e.target.value); setFormNameError(''); }}
                  className={`w-full bg-surface-primary border rounded-lg px-4 py-2 text-content-primary ${
                    formNameError ? 'border-red-500' : 'border-theme-border'
                  }`}
                  placeholder="Ex: Tecnologia"
                  autoFocus
                />
                {formNameError && (
                  <p className="text-red-500 text-sm mt-1">{formNameError}</p>
                )}
              </div>
              <div>
                <label className="block text-sm text-content-muted mb-1">Descrição</label>
                <textarea
                  value={formDescription}
                  onChange={e => setFormDescription(e.target.value)}
                  className="w-full bg-surface-primary border border-theme-border rounded-lg px-4 py-2 text-content-primary resize-none"
                  rows={3}
                  placeholder="Descrição opcional..."
                />
              </div>
              <div>
                <label className="block text-sm text-content-muted mb-1">Ordem</label>
                <input
                  type="number"
                  value={formOrder}
                  onChange={e => setFormOrder(Number(e.target.value))}
                  className="w-full bg-surface-primary border border-theme-border rounded-lg px-4 py-2 text-content-primary"
                  min={0}
                />
              </div>
              <div className="border-t border-theme-border pt-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="sector-agent-feature-toggle"
                    checked={formAgentFeatureEnabled}
                    onChange={(e) => setFormAgentFeatureEnabled(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <span className="text-sm font-medium">
                    Habilitar Hub de Agentes pra todos do setor
                  </span>
                </label>
                <p className="text-xs text-content-muted mt-1 ml-6">
                  Quando ligado, qualquer LEARNER do setor passa a acessar `/agentes`.
                </p>
                {editingSector && formAgentFeatureEnabled !== editingSector.agent_feature_enabled && (
                  <div className="mt-3">
                    <label className="block text-xs text-content-muted mb-1">
                      Motivo (opcional, vai pro audit)
                    </label>
                    <input
                      type="text"
                      data-testid="sector-agent-feature-reason"
                      value={formAgentFeatureReason}
                      onChange={(e) => setFormAgentFeatureReason(e.target.value)}
                      placeholder="Ex.: Piloto Q3 com time de RH"
                      className="w-full bg-surface-primary border border-theme-border rounded-lg px-3 py-1.5 text-content-primary text-sm"
                    />
                  </div>
                )}
              </div>
              <div className="border-t border-theme-border pt-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="sector-seop-feature-toggle"
                    checked={formSeopFeatureEnabled}
                    onChange={(e) => setFormSeopFeatureEnabled(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <span className="text-sm font-medium">
                    Habilitar Balanceamento de Linha pra todos do setor
                  </span>
                </label>
                <p className="text-xs text-content-muted mt-1 ml-6">
                  Quando ligado, qualquer LEARNER do setor passa a acessar
                  `/balanceamento-linha` — que não aparece na Sidebar, só por link direto.
                </p>
                {editingSector && formSeopFeatureEnabled !== editingSector.seop_feature_enabled && (
                  <div className="mt-3">
                    <label className="block text-xs text-content-muted mb-1">
                      Motivo (opcional, vai pro audit)
                    </label>
                    <input
                      type="text"
                      data-testid="sector-seop-feature-reason"
                      value={formSeopFeatureReason}
                      onChange={(e) => setFormSeopFeatureReason(e.target.value)}
                      placeholder="Ex.: piloto com o time de planejamento"
                      className="w-full bg-surface-primary border border-theme-border rounded-lg px-3 py-1.5 text-content-primary text-sm"
                    />
                  </div>
                )}
              </div>
              <div className="border-t border-theme-border pt-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="sector-dashboard-feature-toggle"
                    checked={formDashboardFeatureEnabled}
                    onChange={(e) => setFormDashboardFeatureEnabled(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <span className="text-sm font-medium">
                    Liberar os painéis executivos pra todos do setor
                  </span>
                </label>
                <p className="text-xs text-content-muted mt-1 ml-6">
                  Quando ligado, qualquer membro do setor (CURATOR incluso) passa a abrir
                  `/paineis` — com números de produção e faturamento. Não aparece na Sidebar.
                </p>
                {editingSector && formDashboardFeatureEnabled !== editingSector.dashboard_feature_enabled && (
                  <div className="mt-3">
                    <label className="block text-xs text-content-muted mb-1">
                      Motivo (opcional, vai pro audit)
                    </label>
                    <input
                      type="text"
                      data-testid="sector-dashboard-feature-reason"
                      value={formDashboardFeatureReason}
                      onChange={(e) => setFormDashboardFeatureReason(e.target.value)}
                      placeholder="Ex.: diretoria industrial"
                      className="w-full bg-surface-primary border border-theme-border rounded-lg px-3 py-1.5 text-content-primary text-sm"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 rounded-lg text-content-muted hover:text-content-primary transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={!formName.trim() || formSaving}
                className="bg-accent hover:bg-accent-hover text-accent-contrast px-5 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {formSaving ? 'Salvando...' : editingSector ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingSector && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface-card rounded-2xl border border-theme-border w-full max-w-md p-6">
            <h2 className="text-lg font-bold mb-2">Excluir Setor</h2>
            <p className="text-content-muted mb-4">
              Tem certeza que deseja excluir o setor <strong>{deletingSector.name}</strong>?
              {deletingSector.users_count > 0 && (
                <span className="block mt-2 text-amber-400">
                  Os {deletingSector.users_count} usuários deste setor serão desalocados.
                </span>
              )}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingSector(null)}
                className="px-4 py-2 rounded-lg text-content-muted hover:text-content-primary transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-lg font-medium transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSectorsPage;
